import React, { useEffect, useState, useRef } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";

const HeaderTwo = () => {
  const navigate = useNavigate();

  /* ================= LOGIN STATE ================= */
  const [isLoggedIn, setIsLoggedIn] = useState(
    sessionStorage.getItem("isLoggedIn") === "true"
  );

  useEffect(() => {
    const handleSessionUpdate = () => {
      setIsLoggedIn(sessionStorage.getItem("isLoggedIn") === "true");
    };
    window.addEventListener("sessionUpdated", handleSessionUpdate);
    return () =>
      window.removeEventListener("sessionUpdated", handleSessionUpdate);
  }, []);

  /* ================= CART ================= */
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    const updateCart = () => {
      const cart = JSON.parse(sessionStorage.getItem("cart")) || [];
      setCartCount(cart.reduce((s, i) => s + (i.quantity || 1), 0));
    };
    updateCart();
    window.addEventListener("storage", updateCart);
    window.addEventListener("cartUpdated", updateCart);
    return () => {
      window.removeEventListener("storage", updateCart);
      window.removeEventListener("cartUpdated", updateCart);
    };
  }, []);

  /* ================= SEARCH ================= */
  const [searchQuery, setSearchQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef(null);
  const debounce = useRef(null);

  useEffect(() => {
    fetch("/assets/data/hp-products.json")
      .then((r) => r.json())
      .then(setProducts)
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (!searchOpen) return setShowDropdown(false);
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      if (searchQuery.trim()) {
        const res = products.filter((p) =>
          p.name.toLowerCase().includes(searchQuery.toLowerCase())
        );
        setFiltered(res.slice(0, 8));
        setShowDropdown(true);
      } else setShowDropdown(false);
    }, 350);
  }, [searchQuery, products, searchOpen]);

  const handleSelectProduct = (id) => {
    setSearchQuery("");
    setSearchOpen(false);
    setShowDropdown(false);
    navigate(`/product/${id}`);
  };

  /* ================= MOBILE MENU ================= */
  const [menuOpen, setMenuOpen] = useState(false);
  const toggleMenu = () => setMenuOpen((p) => !p);

  return (
    <>
      {/* ================= STYLES ================= */}
      <style>{`
        /* TOP BAR */
        .sx-topbar{background:#0b5ed7;color:#cbd5e1;font-size:13px}
        .sx-topbar-inner{max-width:1300px;margin:auto;padding:10px 20px;display:flex;justify-content:space-between}
        .sx-topbar i{color:#ffffff}

        /* HEADER */
        .sx-header{background:#000;position:sticky;top:0;z-index:999}
        .sx-row{max-width:1300px;margin:auto;padding:14px 20px;display:flex;align-items:center;justify-content:space-between}

        /* NAV */
        .sx-nav{display:flex;gap:28px}
        .sx-nav a{color:#fff;text-decoration:none;font-size:14px;position:relative}
        .sx-nav a::after{content:"";position:absolute;left:0;bottom:-6px;height:2px;width:0;background:#0b5ed7;transition:.3s}
        .sx-nav a:hover::after,.sx-nav a.active::after{width:100%}

        /* ACTIONS */
        .sx-actions{display:flex;gap:14px}
        .sx-icon{background:none;border:none;cursor:pointer;position:relative}
        .sx-icon i{color:#fff;font-size:18px}
        .sx-icon:hover i{color:#0b5ed7}
        .sx-count{position:absolute;top:-6px;right:-8px;background:#0b5ed7;color:#fff;font-size:11px;padding:2px 6px}

        /* SEARCH */
        .sx-search{background:#000;padding:12px 20px}
        .sx-search input{width:100%;padding:12px;background:#ffffff;border:2px solid grey;color:#000}
        .sx-search input:focus{outline:2px solid #0b5ed7}

        .sx-results{background:#0a0a0a;margin-top:6px}
        .sx-item{padding:10px;display:flex;gap:10px;cursor:pointer}
        .sx-item:hover{background:#0b5ed7}
        .sx-item img{width:40px;height:40px;object-fit:cover}

        /* MOBILE */
        .sx-menu-toggle{display:none}
        @media(max-width:992px){
          .sx-nav{display:none}
          .sx-menu-toggle{display:block}
        }

        /* MOBILE MENU */
        .sx-overlay{position:fixed;inset:0;background:rgba(0,0,0,.6);opacity:${
          menuOpen ? 1 : 0
        };pointer-events:${
        menuOpen ? "auto" : "none"
      };transition:.3s;z-index:998}
        .sx-mobile{position:fixed;top:0;right:0;width:85%;max-width:320px;height:100vh;background:#000;transform:translateX(${
          menuOpen ? "0" : "100%"
        });transition:.3s;z-index:999;display:flex;flex-direction:column}
        .sx-mobile a{padding:14px 16px;color:#fff;text-decoration:none}
        .sx-mobile a:hover{background:#0b5ed7}
      `}</style>

      {/* TOP BAR */}
      <div className="sx-topbar">
        <div className="sx-topbar-inner">
          <span>
            <i className="ph ph-envelope" /> info@awaneez.com
          </span>
          <span>
            <i className="ph ph-phone" /> +1-786-761-8824
          </span>
        </div>
      </div>

      {/* HEADER */}
      <header className="sx-header">
        <div className="sx-row">
          <Link to="/">
            <img
              src="/assets/images/logo/logo-two.png"
              height="42"
              width="80"
              alt="Awaneez LLC"
            />
          </Link>

          <nav className="sx-nav">
            <NavLink to="/" end>
              Home
            </NavLink>
            <NavLink to="/shop">Our Printers</NavLink>
            <NavLink to="/about-us">About Us</NavLink>
            <NavLink to="/contact-us">Contact Us</NavLink>
          </nav>

          <div className="sx-actions">
            <button
              className="sx-icon"
              onClick={() => setSearchOpen(!searchOpen)}
            >
              <i className="ph ph-magnifying-glass" />
            </button>

            <Link to="/cart" className="sx-icon">
              <i className="ph ph-shopping-cart-simple" />
              {cartCount > 0 && <span className="sx-count">{cartCount}</span>}
            </Link>

            <Link
              to={isLoggedIn ? "/my-account" : "/login"}
              className="sx-icon"
            >
              <i className="ph ph-user" />
            </Link>

            <button className="sx-icon sx-menu-toggle" onClick={toggleMenu}>
              <i className="ph ph-list" />
            </button>
          </div>
        </div>

        {searchOpen && (
          <div className="sx-search" ref={searchRef}>
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search printers..."
            />
            {showDropdown && (
              <div className="sx-results">
                {filtered.map((p) => (
                  <div
                    key={p.id}
                    className="sx-item"
                    onClick={() => handleSelectProduct(p.id)}
                  >
                    <img src={p.imageUrl} alt={p.name} />
                    <span>{p.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </header>

      {/* MOBILE MENU */}
      <div className="sx-overlay" onClick={toggleMenu} />
      <aside className="sx-mobile">
        <Link to="/" onClick={toggleMenu}>
          Home
        </Link>
        <Link to="/shop" onClick={toggleMenu}>
          Our Printers
        </Link>
        <Link to="/about-us" onClick={toggleMenu}>
          About Us
        </Link>
        <Link to="/contact-us" onClick={toggleMenu}>
          Contact Us
        </Link>
        <Link to="/cart" onClick={toggleMenu}>
          Cart
        </Link>
        <Link to={isLoggedIn ? "/my-account" : "/login"} onClick={toggleMenu}>
          Account
        </Link>
      </aside>
    </>
  );
};

export default HeaderTwo;
